// src/components/tabs/TabStrip.tsx
// The tab strip of one pane. Every pane has its own, as in JetBrains — with a
// single pane it sits directly under the navbar and reads as part of it.
//
// Dragging uses native HTML5 drag events rather than a drag library: the two
// gestures needed here are reordering within a strip and dropping onto another
// pane, both of which the platform already gives us.

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { tabsStore } from '@/stores/TabsStore';
import type { ContentAlign, ContentWidth, TabGroupNode } from '@/features/tabs/types';

export const TAB_DRAG_TYPE = 'application/x-corde-tab';

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round">
      <path d="M18 6L6 18M6 6l12 12" />
    </svg>
  );
}

function PinIcon({ filled }: { filled: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className="w-3 h-3" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <path d="M12 17v5M9 3h6l-1 8h3l-5 6-5-6h3z" />
    </svg>
  );
}

function SplitIcon({ direction }: { direction: 'row' | 'column' }) {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      {direction === 'row' ? <path d="M12 3v18" /> : <path d="M3 12h18" />}
    </svg>
  );
}

function LayoutIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 4v16M16 4v16" />
    </svg>
  );
}

const WIDTHS: { value: ContentWidth; key: string }[] = [
  { value: 'full', key: 'layout.tabs.width.full' },
  { value: 'contained', key: 'layout.tabs.width.contained' },
];

const ALIGNS: { value: ContentAlign; key: string }[] = [
  { value: 'left', key: 'layout.tabs.align.left' },
  { value: 'center', key: 'layout.tabs.align.center' },
  { value: 'right', key: 'layout.tabs.align.right' },
];

/** Width and alignment of the pane's content, per pane because panes differ in width. */
const PresentationMenu = observer(({ group }: { group: TabGroupNode }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
        title={t('layout.tabs.presentation')}
        aria-label={t('layout.tabs.presentation')}
        aria-expanded={open}
      >
        <LayoutIcon />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute right-0 top-7 z-50 w-44 py-1 rounded-md border border-border bg-popover shadow-md">
            <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
              {t('layout.tabs.widthLabel')}
            </div>
            {WIDTHS.map((w) => (
              <button
                key={w.value}
                type="button"
                onClick={() => tabsStore.setPresentation(group.id, { width: w.value })}
                className={`flex w-full items-center px-2 py-1 text-sm text-left hover:bg-accent ${
                  group.width === w.value ? 'text-foreground font-medium' : 'text-muted-foreground'
                }`}
              >
                {t(w.key)}
              </button>
            ))}
            {group.width === 'contained' && (
              <>
                <div className="mt-1 px-2 py-1 text-xs font-medium text-muted-foreground border-t border-border">
                  {t('layout.tabs.alignLabel')}
                </div>
                {ALIGNS.map((a) => (
                  <button
                    key={a.value}
                    type="button"
                    onClick={() => tabsStore.setPresentation(group.id, { align: a.value })}
                    className={`flex w-full items-center px-2 py-1 text-sm text-left hover:bg-accent ${
                      group.align === a.value ? 'text-foreground font-medium' : 'text-muted-foreground'
                    }`}
                  >
                    {t(a.key)}
                  </button>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
});

export const TabStrip = observer(({ group }: { group: TabGroupNode }) => {
  const { t } = useI18n();
  const tabs = tabsStore.tabsOf(group.id);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [menuTabId, setMenuTabId] = useState<string | null>(null);

  if (tabs.length === 0) return null;

  const isFocusedPane = tabsStore.focusedGroupId === group.id;

  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={`flex items-center h-9 gap-0.5 px-1 border-b bg-background ${
        isFocusedPane ? 'border-border' : 'border-border/50'
      }`}
    >
      {/* Drop handling sits here rather than on the tablist itself: a role that
          announces itself as a tablist must be focusable the moment it also
          handles input, and the strip is not the thing you focus — its tabs are. */}
      <div
        className="flex items-center gap-0.5 min-w-0 overflow-x-auto scrollbar-none"
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes(TAB_DRAG_TYPE)) e.preventDefault();
        }}
        onDrop={(e) => {
          const tabId = e.dataTransfer.getData(TAB_DRAG_TYPE);
          if (tabId) tabsStore.reorderOrMove(tabId, group.id, dropIndex ?? tabs.length);
          setDropIndex(null);
        }}
      >
        {tabs.map((tab, index) => {
          const isActive = group.activeTabId === tab.id;
          return (
            <div
              key={tab.id}
              role="tab"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(TAB_DRAG_TYPE, tab.id);
                e.dataTransfer.effectAllowed = 'move';
              }}
              onDragOver={(e) => {
                if (!e.dataTransfer.types.includes(TAB_DRAG_TYPE)) return;
                e.preventDefault();
                const box = e.currentTarget.getBoundingClientRect();
                setDropIndex(e.clientX < box.left + box.width / 2 ? index : index + 1);
              }}
              onClick={() => tabsStore.activateTab(tab.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  tabsStore.activateTab(tab.id);
                  return;
                }
                if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                  e.preventDefault();
                  const step = e.key === 'ArrowRight' ? 1 : -1;
                  const next = tabs[(index + step + tabs.length) % tabs.length];
                  if (next) tabsStore.activateTab(next.id);
                  return;
                }
                if (e.key === 'Delete' || e.key === 'Backspace') {
                  e.preventDefault();
                  tabsStore.close(tab.id);
                }
              }}
              onAuxClick={(e) => {
                // Middle click closes, as everywhere else tabs exist.
                if (e.button === 1) {
                  e.preventDefault();
                  tabsStore.close(tab.id);
                }
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                setMenuTabId(tab.id);
              }}
              className={`group relative flex items-center gap-1.5 h-7 px-2 rounded-t cursor-default select-none text-sm whitespace-nowrap ${
                isActive
                  ? 'bg-accent text-foreground'
                  : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
              } ${dropIndex === index ? 'ml-2' : ''}`}
              title={tab.path}
            >
              {tab.pinned && <PinIcon filled />}
              <span className="max-w-[12rem] truncate">{tab.title}</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  tabsStore.close(tab.id);
                }}
                className="opacity-0 group-hover:opacity-100 focus:opacity-100 rounded p-0.5 hover:bg-background"
                aria-label={t('layout.tabs.close')}
              >
                <CloseIcon />
              </button>

              {menuTabId === tab.id && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setMenuTabId(null)} aria-hidden />
                  <div className="absolute left-0 top-8 z-50 w-52 py-1 rounded-md border border-border bg-popover shadow-md">
                    {[
                      { key: 'layout.tabs.menu.pin', run: () => tabsStore.togglePinned(tab.id) },
                      { key: 'layout.tabs.menu.close', run: () => tabsStore.close(tab.id) },
                      { key: 'layout.tabs.menu.closeOthers', run: () => tabsStore.closeOthers(group.id, tab.id) },
                      { key: 'layout.tabs.menu.closeRight', run: () => tabsStore.closeToTheRight(group.id, tab.id) },
                      { key: 'layout.tabs.menu.splitRight', run: () => tabsStore.splitWithTab(group.id, tab.id, 'row') },
                      { key: 'layout.tabs.menu.splitDown', run: () => tabsStore.splitWithTab(group.id, tab.id, 'column') },
                    ].map((item) => (
                      <button
                        key={item.key}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          item.run();
                          setMenuTabId(null);
                        }}
                        className="flex w-full px-2 py-1 text-sm text-left text-foreground hover:bg-accent"
                      >
                        {t(item.key)}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-0.5 ml-auto pl-1">
        <button
          type="button"
          onClick={() => tabsStore.splitGroupById(group.id, 'row')}
          className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
          title={t('layout.tabs.menu.splitRight')}
          aria-label={t('layout.tabs.menu.splitRight')}
        >
          <SplitIcon direction="row" />
        </button>
        <button
          type="button"
          onClick={() => tabsStore.splitGroupById(group.id, 'column')}
          className="flex items-center justify-center w-6 h-6 rounded text-muted-foreground hover:text-foreground hover:bg-accent"
          title={t('layout.tabs.menu.splitDown')}
          aria-label={t('layout.tabs.menu.splitDown')}
        >
          <SplitIcon direction="column" />
        </button>
        <PresentationMenu group={group} />
      </div>
    </div>
  );
});
